import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "./TagsItem.module.css";

/**
 * One tag group (Languages, Keyword Tags, ...): the tags currently on the
 * product, the rest of the group to pick from, and a field to add a new one.
 */
const TagGroup = ({ label, selected, available, onAdd, onRemove, creating }) => {
  const [draft, setDraft] = useState("");

  const query = draft.trim().toLowerCase();
  const exactExists = [...selected, ...available].some(
    (name) => name.toLowerCase() === query
  );

  const handleAdd = () => {
    const name = draft.trim().toLowerCase();
    if (!name || creating) return;
    onAdd(name);
    setDraft("");
  };

  const unselected = query
    ? available.filter((name) => name.toLowerCase().includes(query))
    : available;

  return (
    <div className={styles.group}>
      <div className={styles.groupTitle}>{label}</div>

      {selected.length > 0 && (
        <div className={styles.tagList}>
          {selected.map((name) => (
            <span key={name} className={styles.tagChip}>
              {name}
              <button
                type="button"
                className={styles.tagRemove}
                aria-label={`Remove ${name}`}
                onClick={() => onRemove(name)}
                title={`Remove the "${name}" tag from this product.`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.addRow}>
        <input
          className={styles.addInput}
          type="text"
          placeholder={`Add to ${label}…`}
          aria-label={`Add a tag to ${label}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value.toLowerCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <button
          type="button"
          className={styles.addButton}
          onClick={handleAdd}
          disabled={!draft.trim() || creating}
          title={
            exactExists
              ? "Add this existing tag to the product."
              : "Create this new tag and add it to the product."
          }
        >
          {exactExists ? "Add" : "Create"}
        </button>
      </div>

      {unselected.length > 0 && (
        <div className={styles.optionList}>
          {unselected.map((name) => (
            <button
              key={name}
              type="button"
              className={styles.optionChip}
              onClick={() => onAdd(name)}
              title={`Add the "${name}" tag to this product.`}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

TagGroup.propTypes = {
  label: PropTypes.string.isRequired,
  selected: PropTypes.arrayOf(PropTypes.string),
  available: PropTypes.arrayOf(PropTypes.string),
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  creating: PropTypes.bool,
};

TagGroup.defaultProps = {
  selected: [],
  available: [],
  creating: false,
};

export default TagGroup;
